import { useRef, useState } from 'react';
import { useEditMode } from '@/context/EditModeContext';
import { useSiteData } from '@/context/SiteDataContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ImagePlus, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Click-to-replace image or video.
 *   <EditImage field="hero_video" target="settings" accept="video/*" category="hero">
 *     <video ... />  // regular element renders here
 *   </EditImage>
 */
export default function EditImage({ field, target = 'settings', category = 'misc', accept = 'image/*,video/*', children, wrapperClass = '' }) {
  const { editMode } = useEditMode();
  const { refresh, loadPageContent } = useSiteData();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const { data } = await api.post(`/admin/upload?category=${encodeURIComponent(category)}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const url = data.url.startsWith('http') ? data.url : `${BACKEND_URL}${data.url}`;
      if (target === 'settings') {
        await api.patch('/site/settings', { [field]: url });
        refresh(['settings']);
      } else if (target?.startsWith('page:')) {
        const slug = target.slice(5);
        await api.patch(`/site/page-content/${slug}`, { [field]: url });
        loadPageContent(slug);
      }
      toast.success('Replaced');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  if (!editMode) return children;

  return (
    <div className={`relative editable-image ${wrapperClass}`} data-testid={`edit-img-${field}`}>
      {children}
      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        className="absolute top-3 right-3 z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-royal text-white text-xs font-semibold shadow-lg hover:bg-gold hover:text-royal-dark transition disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImagePlus className="w-3 h-3" />}
        {busy ? 'Uploading…' : 'Replace'}
      </button>
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={handle} />
    </div>
  );
}
